import {
    forwardRef,
    useImperativeHandle,
    useState,
    useContext,
    useRef,
} from 'react'

import TextInput from '../../../../Components/TextInput'
import ApplicantDataFormContext from '../../../../Contexts/ApplicantDataFormContext'

// Name validator
const nameValidator = (val) => /^[\p{L} '-]{2,50}$/u.test(val.trim());

const AcknowledgmentAndAuthorization = forwardRef(({ data, company, willVerificationBeApplied, refToPrint }, ref) => {

    const [requestReceiptCopy, setRequestReceiptCopy] = useState(data?.requestReceiptCopy ?? false)
    const [agreeChecked, setAgreeChecked] = useState(data?.agreeChecked ?? false)
    const [lastFourSSN, setLastFourSSN] = useState(data?.lastFourSSN ?? '')
    const [error, setError] = useState("")

    const todayDate = new Date().toLocaleDateString()

    const applicantDataForm = useContext(ApplicantDataFormContext)

    const fullName = applicantDataForm.current["fullName"]
    const fullNameRef = useRef()

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {

            let isValid = agreeChecked
            const fullNameData = fullNameRef.current.validateAndGetData()

            if (!isValid) {
                setError("You need to agree on electronic signature")
            }

            if (isValid) {
                isValid = lastFourSSN.length === 4

                if (!isValid) {
                    setError("You need to fill last 4 digit of your SSN")
                }

            }

            isValid = isValid && fullNameData.isValid

            if (isValid && fullName !== fullNameData.value) {
                isValid = false
                setError("Full Legal Name does not match the one you provided on previous pages.")
            }

            applicantDataForm.current["ssnLastFourDigit"] = lastFourSSN

            if (isValid) {
                setError("")
            }

            return {
                isValid,
                value: {
                    agreeChecked,
                    requestReceiptCopy,
                    lastFourSSN,
                    fullName,
                },
            }
        },
    }))

    return (
        <div className="max-w-3xl mx-auto px-4 py-10">
            <div ref={refToPrint} className="flex flex-col w-full space-y-6">
                <h1 className="text-2xl font-bold text-gray-800 text-center">
                    Acknowledgment and Authorization for Background Check
                </h1>

                <div className="space-y-4 text-gray-700 text-sm sm:text-base">
                    <p>
                        I acknowledge receipt of the separate documents entitled{" "}
                        <strong> {willVerificationBeApplied ? "FCRA - DISCLOSURE FOR INVESTIGATIVE CONSUMER REPORT " : "FCRA - DISCLOSURE REGARDING BACKGROUND INVESTIGATION "} </strong>
                        as well as <strong>A SUMMARY OF YOUR RIGHTS UNDER THE FAIR CREDIT REPORTING ACT</strong>. I certify that I have read and
                        understand those documents.
                    </p>

                    <p>
                        I hereby authorize the obtaining of "{willVerificationBeApplied ? "investigative consumer reports" : "consumer report"}" by
                        <strong> {company?.name}</strong> (the "Company") at any time after receipt of this
                        authorization and throughout my employment, if applicable. To this end, I hereby authorize, without reservation,
                        any law enforcement agency, administrator, state or federal agency, institution, school or university (public or private),
                        information service bureau, employer, or insurance company to furnish any and all background information requested by
                        VerifiedID, <a href="tel:+18887182597" className="text-blue-600 underline">(888) 718-2597</a>,
                        11180 State Bridge Road, suite 303, Alpharetta, GA,
                        30022, USA<a href="https://verifiedid.co" className="text-blue-600 underline" target="_blank">https://verifiedid.co</a> and/or
                        the Company itself. I agree that an electronic or photographic copy of this Authorization shall be as valid as the original.
                    </p>

                    <h2 className="font-semibold text-gray-800 mt-6">Authorization</h2>
                    <p>
                        I understand that the scope of my authorization is not limited to the present and, if I am hired, will continue throughout
                        the course of my employment and allow the Company to conduct future screenings for retention, promotion or reassignment,
                        as permitted by law and unless revoked by me in writing.
                    </p>

                    <h2 className="font-semibold text-gray-800 mt-6">
                        Request for Copy of Investigative Consumer Report
                    </h2>

                    <label className="flex items-start space-x-2">
                        <input
                            type="checkbox"
                            className="mt-1"
                            onChange={() => setRequestReceiptCopy(!requestReceiptCopy)}
                            checked={requestReceiptCopy}
                        />
                        <span>Check this box if you would like to receive a copy of any investigative consumer report obtained by the Company.</span>
                    </label>


                    <h2 className="font-semibold text-gray-800 mt-6">Your Electronic Signature</h2>

                    <p>
                        I understand that by checking the "I AGREE" box below, typing my name and the last four digits of my Social Security Number or
                        National ID Number, and clicking on the "SIGN ACKNOWLEDGMENT" button, this constitutes my electronic signature, dated as of when I click
                        the button. By doing so, I:
                    </p>

                    <ul className="list-disc list-inside space-y-1">
                        <li>
                            Am authorizing {company?.name} to conduct the background check(s) described above.
                        </li>
                        <li>
                            Am consenting to use electronic means to sign this form and confirm that I have read and understand all applicable disclosures
                            and this Authorization.
                        </li>
                        <li>
                            Acknowledge I may request a hard copy of this Disclosure and Authorization form after agreeing to the background check
                            electronically by contacting VerifiedID at <a href="tel:+18887182597" className="text-blue-600 underline">(888) 718-2597</a>.
                        </li>
                    </ul>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 items-end">
                        <div>
                            <TextInput
                                ref={fullNameRef}
                                title={"Full Legal Name"}
                                description="Provide full legal name, including middle name"
                                data={fullName}
                                isMandatory={true}
                                validator={nameValidator}
                            />
                        </div>

                        <div className='my-2' >
                            <label className="text-sm text-gray-600 font-medium">Last 4 digits of SSN/National ID Number*</label>
                            <input
                                value={lastFourSSN}
                                onChange={(e) => setLastFourSSN(e.target.value)}
                                maxLength={4}
                                className="mt-1 w-full border border-gray-300 rounded-md px-4 py-2 text-gray-700"
                                placeholder="1234"
                            />
                        </div>

                        <div>
                            <label className="text-sm text-gray-600 font-medium">Date</label>
                            <input
                                value={todayDate}
                                readOnly
                                className="mt-1 w-full border border-gray-300 rounded-md px-4 py-2 bg-gray-100 text-gray-700"
                            />
                        </div>
                    </div>

                    <label className="flex items-start space-x-2">
                        <input
                            type="checkbox"
                            className="mt-1"
                            onChange={() => setAgreeChecked(!agreeChecked)}
                            checked={agreeChecked}
                        />
                        <span>I AGREE</span>
                    </label>

                </div>
            </div>

            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
        </div>
    )

})

export default AcknowledgmentAndAuthorization
